/**
 * Analytics Types
 *
 * Type definitions for the Firebase Analytics implementation.
 * Based on patterns from myAir web and iOS applications.
 */

/**
 * Analytics mode determines how user data is tracked.
 * - identified: Full tracking with user ID association
 * - anonymous: Tracking without user ID (anonymous events only)
 * - untracked: No analytics tracking at all
 */
export enum AnalyticsMode {
  IDENTIFIED = 'identified',
  ANONYMOUS = 'anonymous',
  UNTRACKED = 'untracked',
}

/**
 * User properties that can be set on the analytics user profile.
 */
export interface UserProperties {
  userId?: string | null;
  userRole?: string;
  centerCount?: number;
  preferredLanguage?: string;
  userRegion?: string;
  appVersion?: string;
}

/**
 * Parameters that can be attached to analytics events.
 */
export type AnalyticsParams = Record<string, string | number | boolean | undefined | null>;

/**
 * Screen view event data.
 */
export interface ScreenViewEvent {
  screenName: string;
  screenClass?: string;
  params?: AnalyticsParams;
}

/**
 * Generic analytics event data.
 */
export interface AnalyticsEvent {
  name: string;
  params?: AnalyticsParams;
}

/**
 * Feature categories for organizing analytics events.
 */
export enum AnalyticsFeature {
  AUTHENTICATION = 'authentication',
  STUDIES = 'studies',
  CENTERS = 'centers',
  PROFILE = 'profile',
  DATA_VIEWER = 'data_viewer',
  REPORTS = 'reports',
  SETTINGS = 'settings',
  NAVIGATION = 'navigation',
  GENERAL = 'general',
  API_CLIENT = 'api_client',
}

/**
 * Login type for tracking authentication methods.
 */
export enum LoginType {
  PASSWORD = 'password',
  TWO_FACTOR = 'two_factor',
  SSO = 'sso',
}

/**
 * Logout cause for tracking why users logged out.
 */
export enum LogoutCause {
  USER_INITIATED = 'user_initiated',
  SESSION_EXPIRED = 'session_expired',
  FORCED = 'forced',
}

/**
 * User role for analytics segmentation.
 */
export enum UserRole {
  ADMIN = 'admin',
  PHYSICIAN = 'physician',
  INTEGRATOR = 'integrator',
  DEVELOPER = 'developer',
}
